import { EntityType } from '@frontend/common';
import { NumberInput } from '@frontend/components/basic-forms';
import { AsyncComponent } from '@frontend/components/common';
import { CommonMessage, useLocale } from '@frontend/lang';
import { defaultMapWithAttributes } from '@frontend/product/badge';
import { CreateProduct, IProduct, ProductType } from '@frontend/product/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import CreateModal from '../../../../modal/create-modal/create-modal.component';
import { FORM_ID, useCreateKitModal } from './create-kit-modal.controller';

const MultiKitProductSelect = React.lazy(() => import('@frontend/product/select').then((module) => ({ default: module.MultiKitProductSelect })));

interface CreateKitModalProps {
    onSubmitSuccess?: (kit: IProduct) => void;
}

export const CreateKitModal = (props: CreateKitModalProps) => {
    const { initialValue, onSubmit, submitDisabled, selectedProducts, setSelectedProducts, quantities, setQuantity } = useCreateKitModal();
    const { locale } = useLocale();

    return (
        <CreateModal<IProduct, CreateProduct>
            formId={FORM_ID}
            initialValue={initialValue}
            order={['name', 'sku', 'ean', 'description', 'user_info']}
            required={['name']}
            type={EntityType.PRODUCT}
            onSubmit={onSubmit}
            onSubmitSuccess={props.onSubmitSuccess}
            label={
                <FormattedMessage
                    defaultMessage='Create kit'
                    id='create-kit-modal.title'
                    description='Title shown in the modal for creating a product kit'
                />
            }
            title={CommonMessage.FORMS.BASIC_INFORMATION}
            append={
                <div className='flex w-full flex-col gap-4 pt-4'>
                    <AsyncComponent>
                        <MultiKitProductSelect
                            label={CommonMessage.OBJECTS.PRODUCT.PLURAL}
                            value={selectedProducts.map((p) => p.id)}
                            onChangeObject={setSelectedProducts}
                            queryParams={{
                                type: [ProductType.EQUIPMENT, ProductType.CONSUMABLE]
                            }}
                        />
                    </AsyncComponent>
                    {selectedProducts.length > 0 && (
                        <div className='flex flex-col gap-2'>
                            {selectedProducts.map((product) => (
                                <div
                                    key={product.id}
                                    className='flex items-end gap-2'>
                                    <div className='text-foreground-default grow self-center text-sm font-medium'>
                                        {defaultMapWithAttributes(product, locale, ['ean', 'sku'])}
                                    </div>
                                    <div className='w-32'>
                                        <NumberInput
                                            id={`kit-child-quantity-${product.id}`}
                                            label={CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY}
                                            value={quantities[product.id] ?? 1}
                                            min={1}
                                            onChange={(v) => setQuantity(product.id, v)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            }
            subForms={
                new Map([
                    [
                        'Translations',
                        {
                            order: ['name_nl_be'],
                            collapsible: true,
                            defaultCollapsed: true,
                            orderTitles: new Map([
                                [CommonMessage.OBJECTS.PRODUCT_KIT.DEFAULT, ['name_nl_be', 'name_en_us', 'name_fr_fr', 'name_de_de']],
                                [
                                    CommonMessage.OBJECTS.PRODUCT.PRODUCT_DESCRIPTION,
                                    ['description_nl_be', 'description_en_us', 'description_fr_fr', 'description_de_de']
                                ],
                                [CommonMessage.OBJECTS.PRODUCT.USER_INFO, ['user_info_nl_be', 'user_info_en_us', 'user_info_fr_fr', 'user_info_de_de']]
                            ])
                        }
                    ]
                ])
            }
            submitDisabled={submitDisabled}
        />
    );
};
